import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../lib/api';
import { invalidateInventory, useBuildings } from '../lib/hooks';
import { modelLabel, PART_KIND_DESCRIPTION, PART_KIND_LABEL, useCatalog } from '../lib/parts';
import type { Part, PartDetail, PartKind } from '../lib/types';
import { useToast } from './toast';
import { Alert, Button, Field, Input, Modal, Segmented, Select, Textarea } from './ui';

interface FormState {
  kind: PartKind;
  name: string;
  partNumber: string;
  manufacturerId: string;
  description: string;
  unitCost: string;
  quantityOnHand: string;
  reorderAt: string;
  buildingId: string;
  roomId: string;
  notes: string;
  compatibleModelIds: string[];
}

export type PartFormDefaults = Partial<Pick<FormState, 'kind' | 'compatibleModelIds'>>;

function toForm(part?: Part | null, defaults?: PartFormDefaults): FormState {
  return {
    kind: part?.kind ?? defaults?.kind ?? 'ACCESSORY',
    name: part?.name ?? '',
    partNumber: part?.partNumber ?? '',
    manufacturerId: part?.manufacturerId ?? '',
    description: part?.description ?? '',
    unitCost: part?.unitCost ?? '',
    quantityOnHand: part ? String(part.quantityOnHand) : '0',
    reorderAt: part?.reorderAt != null ? String(part.reorderAt) : '',
    buildingId: part?.buildingId ?? '',
    roomId: part?.roomId ?? '',
    notes: part?.notes ?? '',
    compatibleModelIds: part?.models.map((model) => model.id) ?? defaults?.compatibleModelIds ?? [],
  };
}

const sectionTitle = 'mb-3 text-xs font-semibold tracking-wide text-slate-500 uppercase';

export function PartFormModal({
  open,
  onClose,
  part,
  defaults,
  onSaved,
}: {
  open: boolean;
  onClose(): void;
  part?: Part | null;
  /** Starting values for a new part, e.g. a repair part for a work order's model. */
  defaults?: PartFormDefaults;
  onSaved?(part: PartDetail): void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const catalog = useCatalog();
  const buildings = useBuildings();
  const [form, setForm] = useState<FormState>(() => toForm(part, defaults));
  const [modelFilter, setModelFilter] = useState('');
  const [error, setError] = useState('');
  const editing = !!part;

  useEffect(() => {
    if (open) {
      setForm(toForm(part, defaults));
      setModelFilter('');
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, part]);

  const set = (key: Exclude<keyof FormState, 'compatibleModelIds' | 'kind'>) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  const rooms = buildings.data?.find((b) => b.id === form.buildingId)?.rooms ?? [];

  const modelsById = useMemo(
    () =>
      new Map(
        (catalog.data ?? []).flatMap((manufacturer) =>
          manufacturer.models.map((model) => [model.id, { ...model, manufacturer: { id: manufacturer.id, name: manufacturer.name } }] as const),
        ),
      ),
    [catalog.data],
  );

  const filteredCatalog = useMemo(() => {
    const q = modelFilter.trim().toLowerCase();
    return (catalog.data ?? [])
      .map((manufacturer) => ({
        ...manufacturer,
        models: manufacturer.models.filter((model) => !q || `${manufacturer.name} ${model.name}`.toLowerCase().includes(q)),
      }))
      .filter((manufacturer) => manufacturer.models.length > 0);
  }, [catalog.data, modelFilter]);

  const toggleModel = (id: string) =>
    setForm((current) => ({
      ...current,
      compatibleModelIds: current.compatibleModelIds.includes(id)
        ? current.compatibleModelIds.filter((modelId) => modelId !== id)
        : [...current.compatibleModelIds, id],
    }));

  const mutation = useMutation({
    mutationFn: () => {
      const { quantityOnHand, ...fields } = form;
      const body = editing ? fields : { ...fields, quantityOnHand: Number(quantityOnHand || 0) };
      return editing ? api.patch<PartDetail>(`/parts/${part!.id}`, body) : api.post<PartDetail>('/parts', body);
    },
    onSuccess: (saved) => {
      invalidateInventory(queryClient);
      toast.success(editing ? `Saved ${saved.name}` : `Added ${saved.name}`);
      onSaved?.(saved);
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    mutation.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={editing ? `Edit ${part!.name}` : 'Add a part'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="part-form" loading={mutation.isPending}>
            {editing ? 'Save changes' : 'Add part'}
          </Button>
        </>
      }
    >
      <form id="part-form" onSubmit={submit} className="space-y-6">
        {error && <Alert tone="red">{error}</Alert>}

        <section className="space-y-2">
          <Segmented
            value={form.kind}
            onChange={(kind) => setForm((current) => ({ ...current, kind }))}
            options={(Object.keys(PART_KIND_LABEL) as PartKind[]).map((kind) => ({ value: kind, label: PART_KIND_LABEL[kind] }))}
          />
          <p className="text-xs text-slate-500">{PART_KIND_DESCRIPTION[form.kind]}</p>
        </section>

        <section className="grid gap-4 sm:grid-cols-2">
          <Field label="Name" required>
            <Input
              value={form.name}
              onChange={set('name')}
              maxLength={200}
              required
              autoFocus
              placeholder={form.kind === 'ACCESSORY' ? 'e.g. USB-C docking station' : 'e.g. 11.6" LCD screen'}
            />
          </Field>
          <Field label="Part number">
            <Input value={form.partNumber} onChange={set('partNumber')} maxLength={120} className="font-mono" />
          </Field>
          <Field label="Manufacturer">
            <Select value={form.manufacturerId} onChange={set('manufacturerId')}>
              <option value="">Not set</option>
              {catalog.data?.map((manufacturer) => (
                <option key={manufacturer.id} value={manufacturer.id}>
                  {manufacturer.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Description">
            <Input value={form.description} onChange={set('description')} maxLength={2000} />
          </Field>
        </section>

        <section>
          <h3 className={sectionTitle}>Fits these models</h3>
          <p className="mb-2 text-xs text-slate-500">
            Work orders for these models list this part. Leave empty if it fits any device, like a USB mouse.
          </p>
          {form.compatibleModelIds.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {form.compatibleModelIds.map((id) => {
                const model = modelsById.get(id);
                return (
                  <span key={id} className="inline-flex items-center gap-1 rounded-md bg-brand-50 py-0.5 pr-1 pl-2 text-xs font-medium text-brand-800 ring-1 ring-brand-600/20 ring-inset">
                    {model ? modelLabel(model) : 'Unknown model'}
                    <button type="button" onClick={() => toggleModel(id)} className="rounded p-0.5 hover:bg-brand-100" aria-label="Remove model">
                      <X className="size-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
          <div className="rounded-lg border border-slate-200">
            <div className="relative border-b border-slate-100 p-2">
              <Search className="pointer-events-none absolute top-1/2 left-4.5 size-4 -translate-y-1/2 text-slate-400" />
              <Input value={modelFilter} onChange={(e) => setModelFilter(e.target.value)} placeholder="Filter models" className="pl-8" />
            </div>
            <div className="max-h-52 overflow-y-auto py-1">
              {catalog.isLoading ? (
                <p className="px-3 py-2 text-sm text-slate-500">Loading models…</p>
              ) : filteredCatalog.length === 0 ? (
                <p className="px-3 py-2 text-sm text-slate-500">
                  {catalog.data?.length ? 'No models match' : 'No models yet. Add them under Settings → Makes & models.'}
                </p>
              ) : (
                filteredCatalog.map((manufacturer) => (
                  <div key={manufacturer.id}>
                    <p className="px-3 pt-2 pb-1 text-[11px] font-semibold tracking-wide text-slate-400 uppercase">{manufacturer.name}</p>
                    {manufacturer.models.map((model) => (
                      <label key={model.id} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
                        <input
                          type="checkbox"
                          checked={form.compatibleModelIds.includes(model.id)}
                          onChange={() => toggleModel(model.id)}
                          className="size-4 rounded border-slate-300"
                        />
                        {model.name}
                        {model.assetCount > 0 && <span className="text-xs text-slate-400">{model.assetCount} assets</span>}
                      </label>
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>
        </section>

        <section>
          <h3 className={sectionTitle}>Stock</h3>
          <div className="grid gap-4 sm:grid-cols-3">
            {!editing && (
              <Field label="On hand now">
                <Input type="number" inputMode="numeric" min={0} value={form.quantityOnHand} onChange={set('quantityOnHand')} />
              </Field>
            )}
            <Field label="Low stock at" hint="Flag when this many or fewer are left">
              <Input type="number" inputMode="numeric" min={0} value={form.reorderAt} onChange={set('reorderAt')} placeholder="Optional" />
            </Field>
            <Field label="Unit cost">
              <Input inputMode="decimal" value={form.unitCost} onChange={set('unitCost')} placeholder="0.00" />
            </Field>
          </div>
          {editing && <p className="mt-2 text-xs text-slate-500">To change the count, use Update stock on the part’s page.</p>}
        </section>

        <section>
          <h3 className={sectionTitle}>Kept in</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Building">
              <Select value={form.buildingId} onChange={(e) => setForm((current) => ({ ...current, buildingId: e.target.value, roomId: '' }))}>
                <option value="">Not set</option>
                {buildings.data?.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Room">
              <Select value={form.roomId} onChange={set('roomId')} disabled={!form.buildingId}>
                <option value="">Not set</option>
                {rooms.map((room) => (
                  <option key={room.id} value={room.id}>
                    {room.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </section>

        <Field label="Notes">
          <Textarea value={form.notes} onChange={set('notes')} maxLength={5000} rows={2} placeholder="e.g. Bin 4, top shelf" />
        </Field>
      </form>
    </Modal>
  );
}
